import { GameSettingType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { Slider, SoundButton } from "@/shared/ui";
import getTranslationSettingString from "@/domains/playerMenu/helpers/getTranslationSettingString";
import useGameSettings from "@/domains/settings/hooks/useGameSettings";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import PlayerMenuControlLabel from "@/domains/playerMenu/components/PlayerMenuControlLabel";
import useSoundValues from "@/domains/settings/sound/hooks/useSoundValues";
import {
  appliedSoundSettingsSelector,
  reset,
  setAppliedSettings,
  setMemoizedSettings,
} from "@/domains/settings/sound/slice";

const sliderValues = [
  { label: "0", value: 0 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "40", value: 40 },
  { label: "50", value: 50 },
  { label: "60", value: 60 },
  { label: "70", value: 70 },
  { label: "80", value: 80 },
  { label: "90", value: 90 },
  { label: "100", value: 100 },
];

export default function PlayerMenuSettingsSoundContainer() {
  const { soundValues } = useSoundValues();
  const { t } = useTranslation("playerMenu");
  const { updateGameSettings, resetGameSettings, getGameSettings } = useGameSettings();
  const [isSaveSettings, setIsSaveSettings] = useState(false);
  const [isResetSettings, setIsResetSettings] = useState(false);
  const dispatch = useAppDispatch();
  const appliedSoundSettings = useAppSelector(appliedSoundSettingsSelector);

  useEffect(() => {
    dispatch(setMemoizedSettings(appliedSoundSettings));

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsResetSettings(true);
    const settings = Object.keys(appliedSoundSettings);
    const response = await resetGameSettings(settings as unknown as GameSettingType[]);

    if (response instanceof RpcMpProviderSuccessResponse) {
      await getGameSettings();
      dispatch(setMemoizedSettings(appliedSoundSettings));
    } else {
      toast.error(response.message);
    }
    setIsResetSettings(false);
  }, [dispatch, appliedSoundSettings]);

  const saveHandler = useCallback(async () => {
    setIsSaveSettings(true);
    const response = await updateGameSettings(appliedSoundSettings);

    if (response instanceof RpcMpProviderSuccessResponse) {
      dispatch(setMemoizedSettings(appliedSoundSettings));
    } else {
      toast.error(response.message);
    }
    setIsSaveSettings(false);
  }, [appliedSoundSettings, dispatch]);

  const header = <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Sound")}</PlayerMenuContentTitle>;

  const footer = (
    <div className="flex items-center justify-between">
      <SoundButton
        className="h-[2.75rem]"
        loading={isSaveSettings}
        disabled={isSaveSettings || isResetSettings}
        onClick={saveHandler}
      >
        Сохранить изменения
      </SoundButton>
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem]"
        loading={isResetSettings}
        disabled={isResetSettings || isSaveSettings}
        onClick={resetToDefaultHandler}
      >
        Сбрость к стандартным
      </SoundButton>
    </div>
  );

  if (!appliedSoundSettings) {
    return null;
  }

  return (
    <PlayerMenuContent header={header} footer={footer}>
      <div className="grid grid-cols-2 gap-5">
        {soundValues.map((item) => (
          <div key={item}>
            <div className="flex items-center justify-between">
              <PlayerMenuControlLabel>
                {t(getTranslationSettingString(item))}
              </PlayerMenuControlLabel>
              {/* <PlayerMenuTooltip text={""} /> */}
            </div>
            <Slider
              value={appliedSoundSettings[item]}
              values={sliderValues}
              min={0}
              max={100}
              step={1}
              onChange={(value) => dispatch(setAppliedSettings({ [item]: value }))}
              className="mt-3"
              classNameSlider="mt-3"
              classNameLabel="text-12 font-bold"
            />
          </div>
        ))}
      </div>
    </PlayerMenuContent>
  );
}
